import React from 'react';
import { SubmitBlocker, SubmitEligibilityInput, SubmitPhase } from 'services/businessBilling/canSubmit';
import { BusinessLookupEligible, BusinessSubmissionRecovery, BusinessSubmitRequest, BusinessTransportResult } from 'services/businessBilling/contract';
import { createIdempotencyKeyStore, newIdempotencyKey, submissionIdentity } from 'services/businessBilling/idempotency';
import { ParsedBusinessResult, UNKNOWN_NETWORK_RESULT, parseBusinessTransportResult } from 'services/businessBilling/parseResponse';
import { retryDelayMs } from 'services/businessBilling/retryPolicy';
import { buildBusinessSubmitRequest } from 'services/businessBilling/submitRequest';
import { useCanSubmit } from './useCanSubmit';
import { useCooldown } from './useCooldown';
import { useRequestGuard } from './useRequestGuard';

export interface BusinessSubmitOptions {
    rawCui: string;
    lookup: SubmitEligibilityInput['lookup'];
    scopeKey: string; // Session and journey: a change forgets everything about the submit.
    verificationToken: string;
    sessionUsable: boolean;
    submit: (request: BusinessSubmitRequest) => Promise<BusinessTransportResult>;
    createKey?: () => string; // Test seam; a UUID by default.
    recovery?: BusinessSubmissionRecovery | null;
    expiresAt?: string; // Original session expiry, also for an unchanged pending/unknown retry.
}

export interface BusinessSubmit {
    phase: SubmitPhase;
    result?: ParsedBusinessResult; // The answer of the latest submit for the verification now shown (or the success).
    allowed: boolean;
    blockers: SubmitBlocker[];
    submit: () => void;
    pending: boolean; // The original attempt may already be saved; replacement needs a definitive server answer.
}

interface Outcome {
    scope: string;
    identity: string;
    final: 'submitted' | 'rejected' | 'open'; // open: retryable failure or unknown, the next click is an unchanged retry.
    result: ParsedBusinessResult;
    answeredAt: number;
    request: BusinessSubmissionRecovery;
}

const eligibleResponse = (lookup: SubmitEligibilityInput['lookup']): BusinessLookupEligible | null =>
    lookup.phase === 'done' && lookup.result?.kind === 'eligible'
        ? lookup.result.response
        : null;

/**
 * Sends the business submit once, with one idempotency key per logical
 * submission (scope + CUI + verification reference). The key is generated at the
 * first attempt and reused, with the exact same body, by every retry of that
 * submission; a new verification means a new key. Two clicks in the same tick
 * send one request. A definitive rejection closes the button until a new lookup;
 * an uncertain or retryable outcome keeps it open for an unchanged retry. The
 * answer is recorded against the submission it belongs to, so a changed CUI never
 * hides a success.
 * A pending submission restored by the authenticated session GET uses its saved
 * key and business identity instead. It shares the same retry and outcome
 * handling, and never generates a replacement key or repeats eligibility lookup.
 */
export const useBusinessSubmit = (options: BusinessSubmitOptions): BusinessSubmit => {
    const { rawCui, lookup, scopeKey, verificationToken, sessionUsable, recovery: restoredRecovery } = options;
    const guard = useRequestGuard(scopeKey);

    const createKeyRef = React.useRef(options.createKey);
    createKeyRef.current = options.createKey;
    const keyStore = React.useRef(
        createIdempotencyKeyStore(() => (createKeyRef.current ? createKeyRef.current() : newIdempotencyKey())),
    );
    const lock = React.useRef(false);

    const [inFlightScope, setInFlightScope] = React.useState<string | null>(null);
    const [outcome, setOutcome] = React.useState<Outcome | null>(null);
    const [sessionBlockedScope, setSessionBlockedScope] = React.useState<string | null>(null);

    const response = eligibleResponse(lookup);
    const outcomeInScope = outcome && outcome.scope === scopeKey ? outcome : null;
    const retained = outcomeInScope && (outcomeInScope.result.kind === 'unknown'
        || (outcomeInScope.result.kind === 'failure' && outcomeInScope.result.response.allowedActions.length === 0))
        ? outcomeInScope.request : null;
    const recovery = restoredRecovery ?? retained;
    const identity = recovery ? submissionIdentity(scopeKey, recovery.business.cui, recovery.business.verificationReference) : response
        ? submissionIdentity(scopeKey, response.company.tax.cui, response.verification.reference)
        : null;

    let phase: SubmitPhase = 'idle';
    if (outcomeInScope?.final === 'submitted') {
        phase = 'submitted';
    }
    else if (inFlightScope === scopeKey) {
        phase = 'in_flight';
    }
    else if (outcomeInScope?.final === 'rejected' && outcomeInScope.identity === identity) {
        phase = 'rejected';
    }

    const pending = phase !== 'submitted' && Boolean(recovery);
    const retryUnexpired = useCooldown(pending ? Date.parse(options.expiresAt ?? '') : null);
    const can = useCanSubmit({
        rawCui,
        lookup,
        sessionUsable: sessionUsable && sessionBlockedScope !== scopeKey && (!pending || retryUnexpired),
        submitPhase: phase,
        recovery,
    });

    // After an uncertain or retryable answer the unchanged retry waits (the server's delay, at least 1 s).
    const retryAt = outcomeInScope?.final === 'open' && outcomeInScope.identity === identity
        ? outcomeInScope.answeredAt + retryDelayMs(outcomeInScope.result)
        : null;
    const waiting = useCooldown(retryAt);
    const allowed = can.allowed && !waiting;
    const blockers: SubmitBlocker[] = waiting ? [...can.blockers, 'retry_wait'] : can.blockers;

    const latest = React.useRef({ allowed: false, response, recovery, identity, scopeKey, verificationToken });
    latest.current = { allowed, response, recovery, identity, scopeKey, verificationToken };

    const submitRef = React.useRef(options.submit);
    submitRef.current = options.submit;

    // Leaving a scope frees the lock and the in-flight marker (its answer is ignored). Answers and session errors
    // stay recorded per scope on purpose: coming back to a submitted or expired session must not re-open it.
    React.useEffect(
        () => () => {
            lock.current = false;
            setInFlightScope(null);
        },
        [scopeKey],
    );

    const submit = React.useCallback(() => {
        const current = latest.current;
        if (lock.current || !current.allowed || (!current.response && !current.recovery) || current.identity === null) {
            return;
        }
        lock.current = true;
        const { scopeKey: scope, identity: submittedIdentity } = current;
        const request: BusinessSubmitRequest = current.recovery ? {
            ...current.recovery, verificationToken: current.verificationToken,
        } : buildBusinessSubmitRequest({
            verificationToken: current.verificationToken,
            response: current.response!,
            idempotencyKey: keyStore.current.keyFor(submittedIdentity),
        });
        const isCurrent = guard.begin();
        // Keep the exact pending identity through an in-flight retry. A later definitive answer can release it.
        if (!current.recovery) setOutcome(null);
        setInFlightScope(scope);

        submitRef.current(request).then(
            (answer) => parseBusinessTransportResult(answer, 'submit'),
            () => UNKNOWN_NETWORK_RESULT,
        ).then((result) => {

            if (!isCurrent()) {
                return;
            }

            lock.current = false;
            setInFlightScope(null);
            let final: Outcome['final'] = 'open';

            if (result.kind === 'submitted') {
                final = 'submitted';
            }
            else if (result.kind === 'failure') {
                if (result.response.outcome === 'session_error') {
                    setSessionBlockedScope(scope);
                }
                final = result.response.error.retryable ? 'open' : 'rejected';
            }

            setOutcome({ scope, identity: submittedIdentity, final, result, answeredAt: Date.now(),
                request: { idempotencyKey: request.idempotencyKey, business: request.business } });

        });
    }, [guard]);

    const result = phase !== 'in_flight' && outcomeInScope && (outcomeInScope.final === 'submitted' || outcomeInScope.identity === identity)
        ? outcomeInScope.result
        : undefined;

    return { phase, result, allowed, blockers, submit, pending };
};
